import {
    isStaffSignatureFieldName,
    PdfFieldType,
    PdfFieldValues,
} from '../../helpers/eicfFillablePdf.ts';

export const EICF_FIELD_ROLES = {
    Patient: 'patient',
    Hcp: 'hcp',
    Display: 'display',
} as const;

export type EICFFieldRole =
    (typeof EICF_FIELD_ROLES)[keyof typeof EICF_FIELD_ROLES];

export interface FieldRule {
    /** Who fills this field. Required-ness now comes from the PDF, not the admin. */
    role: EICFFieldRole;
}

export type FieldConfigMap = Record<string, FieldRule>;

/** Parse a template's FieldConfigJson into a field-name → rule map (tolerant of null/garbage). */
export const parseFieldConfig = (json?: string | null): FieldConfigMap => {
    if (!json) return {};
    try {
        const parsed = JSON.parse(json) as { fields?: FieldConfigMap };
        return parsed?.fields ?? {};
    } catch {
        return {};
    }
};

export const serializeFieldConfig = (map: FieldConfigMap): string =>
    JSON.stringify({ fields: map });

/**
 * Names of the role's required fields that are not yet satisfied: empty text,
 * unchecked checkbox, or an unplaced signature/initials (placedFieldNames lists
 * the fields whose signature image has been dropped). Required-ness comes from
 * the PDF (requiredFieldNames — the fields the document marks Required); only the
 * fields whose configured role matches are validated. A required field with no
 * configured role is attributed to the patient so it is never silently dropped.
 */
export const findMissingRequiredFields = (
    config: FieldConfigMap,
    values: PdfFieldValues,
    placedFieldNames: string[],
    requiredFieldNames: string[],
    role: EICFFieldRole = EICF_FIELD_ROLES.Patient,
): string[] => {
    const placed = new Set(placedFieldNames);
    const roleOf = (name: string): EICFFieldRole =>
        config[name]?.role ?? EICF_FIELD_ROLES.Patient;
    return requiredFieldNames
        .filter((name) => roleOf(name) === role)
        .filter((name) => {
            if (placed.has(name)) return false;
            const value = values[name];
            if (typeof value === 'boolean') return !value;
            return value === undefined || String(value).trim() === '';
        });
};

/**
 * Sensible default role for a freshly-discovered field. A staff/clinician-named
 * signature (e.g. "Investigator Signature") defaults to the HCP role so it is
 * never demanded of the patient — otherwise a multi-signature sponsor PDF would
 * block the patient on a field only the clinician can complete. Everything else
 * defaults to the patient. The admin can still override any default.
 */
export const defaultRuleForType = (
    type: PdfFieldType,
    fieldName = '',
): FieldRule =>
    type === 'signature' && isStaffSignatureFieldName(fieldName)
        ? { role: EICF_FIELD_ROLES.Hcp }
        : { role: EICF_FIELD_ROLES.Patient };

/**
 * The signature field the admin explicitly assigned to {@link role}, chosen only
 * among the actual signature fields. Lets the authoritative per-field role drive
 * the signing target instead of brittle field-name heuristics; returns null when
 * no signature field carries that role (callers fall back to the heuristic).
 */
export const configuredSignatureFieldForRole = (
    signatureFieldNames: string[],
    config: FieldConfigMap,
    role: EICFFieldRole,
): string | null =>
    signatureFieldNames.find((name) => config[name]?.role === role) ?? null;
